import { accessNames } from "common/constants/accessNames";
import { routePaths } from "common/constants/routePaths";
import { haveAccess } from "common/helpers/haveAccess";
import { AuthPage } from "pages/auth/AuthPage";
import { lazy, Suspense, useEffect } from "react";
import { ReactTitle } from "react-meta-tags";
import { useSelector } from "react-redux";
import { Route, Routes, useLocation } from "react-router-dom";
import { accessTokenSelector, rightsSelector, userNameSelector } from "store/auth/authSelectors";
import { authAction } from "store/auth/authSlice";
import styled from "styled-components";
import { AppLayout } from "ui/AppLayout";
import { AuthLayout } from "ui/AuthLayout";
import { Redirect } from "ui/Redirect";
import { IMenu } from "ui/Sidebar/SidebarList";
import { identityRepository } from "../api/identityRepository";
import { useAppDispatch } from "../store";
import { Loyalty } from "./loyalty/Loyalty";
import { Matches } from "./matches/Matches";
import { MatchesPromo } from "./matchesPromo/matchesPromo";
import { Media } from "./media/Media";
import { MediaCategories } from "./mediaCategories/MediaCategories";
import { SpecialOffer } from "./specialOffer/SpecialOffer";
import { Tournaments } from "./tournaments/Tournaments";

const Concert = lazy(async () =>
  import("./concert/Concert").then((module) => ({
    default: module.Concert,
  }))
);

const Quiz = lazy(async () =>
  import("./queez/Quiz").then((module) => ({
    default: module.Quiz,
  }))
);

const StoryLayout = lazy(async () =>
  import("./story/StoryLayout").then((module) => ({
    default: module.StoryLayout,
  }))
);

const PagesSections = lazy(async () =>
  import("./pagesSections/PagesSections").then((module) => ({
    default: module.PagesSections,
  }))
);

const Partners = lazy(async () =>
  import("./partners/Partners").then((module) => ({
    default: module.Partners,
  }))
);

const DirStadiums = lazy(async () =>
  import("./dirStadiums/DirStadiums").then((module) => ({
    default: module.DirStadiums,
  }))
);

const DirTeams = lazy(async () =>
  import("./dirTeams/DirTeams").then((module) => ({
    default: module.DirTeams,
  }))
);

const Polls = lazy(async () =>
  import("./polls/polls").then((module) => ({
    default: module.Polls,
  }))
);

const ClubsTeams = lazy(async () =>
  import("./clubsTeams/ClubsTeams").then((module) => ({
    default: module.ClubsTeams,
  }))
);

const ClubsPlayers = lazy(async () =>
  import("./clubsPlayers/ClubsPlayers").then((module) => ({
    default: module.ClubsPlayers,
  }))
);

const ClubsTrainers = lazy(async () =>
  import("./clubsTrainers/ClubsTrainers").then((module) => ({
    default: module.ClubsTrainers,
  }))
);

const ClubsStaff = lazy(async () =>
  import("./clubsStaff/ClubsStaff").then((module) => ({
    default: module.ClubsStaff,
  }))
);
const ClubsMedical = lazy(async () =>
  import("./clubsMedical/clubsMedical").then((module) => ({
    default: module.ClubsMedical,
  }))
);
const ClubsLeadership = lazy(async () =>
  import("./clubsLeadership/ClubsLeadership").then((module) => ({
    default: module.ClubsLeadership,
  }))
);

const ClubsScouts = lazy(async () =>
  import("./clubsScouts/ClubsScouts").then((module) => ({
    default: module.ClubsScouts,
  }))
);

const EmployeeView = lazy(async () =>
  import("./employeeView/EmployeeView").then((module) => ({
    default: module.EmployeeView,
  }))
);

const EmployeeRole = lazy(async () =>
  import("./employeeRole/EmployeeRole").then((module) => ({
    default: module.EmployeeRole,
  }))
);

const Matching = lazy(async () =>
  import("./matching/Matching").then((module) => ({
    default: module.Matching,
  }))
);

const MatchPromos = lazy(async () =>
  import("./matchesPromo/matchesPromo").then((module) => ({
    default: module.MatchesPromo,
  }))
);

const SystemLog = lazy(async () =>
  import("./systemLog/SystemLog").then((module) => ({
    default: module.SystemLog,
  }))
);

const NotificationsClub = lazy(async () =>
  import("./notifications/notificationsClub/NotificationsClub").then((module) => ({
    default: module.NotificationsClub,
  }))
);

const NotificationsCRM = lazy(async () =>
  import("./notifications/notificationsCRM/NotificationsCRM").then((module) => ({
    default: module.NotificationsCRM,
  }))
);

const Predictor = lazy(async () =>
  import("./predictor/Predictor").then((module) => ({
    default: module.Predictor,
  }))
);

// const PredictorInformation = lazy(async () =>
//   import("./predictorInformation/PredictorInformationModule").then((module) => ({
//     default: module.PredictorInformationModule,
//   }))
// );

const OrganizationsCallForFootball = lazy(async () =>
  import("./callForFootball/Organizations").then((module) => ({
    default: module.OrganizationsCallForFootball,
  }))
);

const DevisionsCallForFootball = lazy(async () =>
  import("./callForFootball/Devisions").then((module) => ({
    default: module.DevisionsCallForFootball,
  }))
);

const LevelsCallForFootball = lazy(async () =>
  import("./callForFootball/Levels").then((module) => ({
    default: module.LevelsCallForFootball,
  }))
);

const MatchApplicationCallForFootball = lazy(async () =>
  import("./callForFootball/MatchApplication").then((module) => ({
    default: module.MatchApplicationCallForFootball,
  }))
);

const MatchApplicationsCallForFootball = lazy(async () =>
  import("./callForFootball/MatchApplications").then((module) => ({
    default: module.MatchApplicationsCallForFootball,
  }))
);
const MatchApplicationsParticipantsCallForFootball = lazy(async () =>
  import("./callForFootball/MatchApplicationsParticipants").then((module) => ({
    default: module.MatchApplicationsParticipantsCallForFootball,
  }))
);

const RepresentativesCallForFootball = lazy(async () =>
  import("./callForFootball/Representatives").then((module) => ({
    default: module.RepresentativesCallForFootball,
  }))
);

const RepresentativeDetailsCallForFootball = lazy(async () =>
  import("./callForFootball/RepresentativeDetails").then((module) => ({
    default: module.RepresentativeDetailsCallForFootball,
  }))
);
const ParticipantsCallForFootball = lazy(async () =>
  import("./callForFootball/Participants").then((module) => ({
    default: module.ParticipantsCallForFootball,
  }))
);
const TagsCallForFootball = lazy(async () =>
  import("./callForFootball/Tags").then((module) => ({
    default: module.TagsCallForFootball,
  }))
);

const Calendar = lazy(async () =>
  import("./calendar/Calendar").then((module) => ({
    default: module.Calendar,
  }))
);

const Banners = lazy(async () => import("./banners/Banners").then((module) => ({ default: module.Banners })));

const BestPlayer = lazy(async () =>
  import("./bestPlayer/BestPlayer").then((module) => ({
    default: module.BestPlayer,
  }))
);

const UsersView = lazy(async () =>
  import("./usersView/UsersView").then((module) => ({
    default: module.UsersView,
  }))
);

const Graduates = lazy(async () =>
  import("./graduates/Graduates").then((module) => ({
    default: module.Graduates,
  }))
);

const GraduateSections = lazy(async () =>
  import("./graduateSections/GraduateSections").then((module) => ({
    default: module.GraduateSections,
  }))
);

const Subscriptions = lazy(async () =>
  import("./pagesSubscriptions/subscriptions").then((module) => ({
    default: module.Subscriptions,
  }))
);
const Rivals = lazy(async () =>
  import("./rivals").then((module) => ({
    default: module.Rivals,
  }))
);

const BidToExit = lazy(async () =>
  import("./bidToExit/bidToExit").then((module) => ({
    default: module.BidToExit,
  }))
);

const Comics = lazy(async () => import("./comics/comics").then((module) => ({ default: module.Comics })));

const ComicSeasons = lazy(async () =>
  import("./comicSeasons/comicSeasons").then((module) => ({
    default: module.ComicSeasons,
  }))
);

export const Pages = () => {
  const { pathname } = useLocation();
  const token = useSelector(accessTokenSelector);
  const name = useSelector(userNameSelector);
  const needLogin = pathname.includes("invitation") || !token || !/authorization=true/.test(document.cookie);
  const rights = useSelector(rightsSelector);
  const dispatch = useAppDispatch();

  useEffect(() => {
    identityRepository.getUserInfo(token).then((res) => dispatch(authAction.updateUserInfo(res.policy)));
  }, []);

  const canView = (access: IMenu["policy"]) => haveAccess(rights, access);

  return (
    <Suspense fallback={<FallBack />}>
      <ReactTitle title={`App ${name ? `- ${name}` : ""}`} />

      <Routes>
        <Route path={routePaths.base} element={needLogin ? <AuthLayout /> : <AppLayout />}>
          {needLogin ? (
            <Route path={routePaths.sign.auth + "/*"} element={<AuthPage />} />
          ) : (
            <>
              {canView(accessNames.story) && [
                <Route key="story" path={routePaths.story + "/*"} element={<StoryLayout />} />,
              ]}
              {canView(accessNames.concert) && [
                <Route key="concert" path={routePaths.concert + "/*"} element={<Concert />} />,
              ]}

              {canView(accessNames.media) && [
                <Route key="1" path={routePaths.media + "/*"} element={<Media access />} />,
                <Route key="2" path={routePaths.mediaCategories + "/*"} element={<MediaCategories access />} />,
              ]}

              {canView(accessNames.cms) && [
                <Route key="1" path={routePaths.pagesSections + "/*"} element={<PagesSections access />} />,
                <Route key="2" path={routePaths.pagesSubscriptions + "/*"} element={<Subscriptions access />} />,
                <Route key="3" path={routePaths.pagesRivals + "/*"} element={<Rivals access />} />,
              ]}

              {canView(accessNames.partner) && (
                <Route path={routePaths.partners + "/*"} element={<Partners access />} />
              )}

              {canView(accessNames.calendar) && (
                <Route path={routePaths.calendar + "/*"} element={<Calendar access />} />
              )}

              {canView(accessNames.tournament) && [
                <Route key="1" path={routePaths.tournaments() + "/*"} element={<Tournaments access />} />,
                <Route key="2" path={routePaths.matches() + "/*"} element={<Matches access />} />,
                <Route key="3" path={routePaths.matching + "/*"} element={<Matching access />} />,
                <Route key="4" path={routePaths.matchesPromo + "/*"} element={<MatchesPromo access />} />,
              ]}

              {canView(accessNames.club) && [
                <Route key="5" path={routePaths.clubsTeams + "/*"} element={<ClubsTeams access />} />,
                <Route key="6" path={routePaths.clubsPlayers + "/*"} element={<ClubsPlayers access />} />,
                <Route key="7" path={routePaths.clubsTrainers + "/*"} element={<ClubsTrainers access />} />,
                <Route key="8" path={routePaths.clubsStaff + "/*"} element={<ClubsStaff access />} />,
                <Route key="9" path={routePaths.clubsLeadership + "/*"} element={<ClubsLeadership access />} />,
                <Route key="9" path={routePaths.clubsScouts + "/*"} element={<ClubsScouts access />} />,
              ]}

              {(canView(accessNames.clubSite) || canView(accessNames.tournament)) && [
                <Route key="1" path={routePaths.dirStadiums + "/*"} element={<DirStadiums access />} />,
                <Route key="2" path={routePaths.dirTeams + "/*"} element={<DirTeams access />} />,
              ]}

              {canView(accessNames.clubSite) && (
                <Route key="10" path={routePaths.clubsMedical + "/*"} element={<ClubsMedical access />} />
              )}

              {canView(accessNames.graduate) && [
                <Route key="1" path={routePaths.graduates + "/*"} element={<Graduates access />} />,
                <Route key="2" path={routePaths.graduateSections + "/*"} element={<GraduateSections access />} />,
              ]}

              {canView(accessNames.bindToExit) && (
                <Route key="1" path={routePaths.kidsBidToExit + "/*"} element={<BidToExit access />} />
              )}

              {canView(accessNames.specialOffer) && [
                <Route key="1" path={routePaths.specialOffer + "/*"} element={<SpecialOffer access />} />,
                <Route key="2" path={routePaths.quiz + "/*"} element={<Quiz access />} />,
                <Route key="3" path={routePaths.polls + "/*"} element={<Polls access />} />,
              ]}

              {canView(accessNames.kids) && [
                <Route key="2" path={routePaths.kidsJournals + "/*"} element={<Comics access />} />,
                <Route key="3" path={routePaths.kidsSeasons + "/*"} element={<ComicSeasons access />} />,
              ]}

              {canView(accessNames.notificationAdmin) && (
                <Route path={routePaths.notificationsClub() + "/*"} element={<NotificationsClub access />} />
              )}
              {canView(accessNames.notificationAdmin) && (
                <Route path={routePaths.notificationsCRM() + "/*"} element={<NotificationsCRM access />} />
              )}

              {/*{canView(accessNames.predictor) && [*/}
              {/*  <Route path={routePaths.predictor + "/*"} element={<Predictor access />} />,*/}
              {/*  <Route path={routePaths.information + "/*"} element={<PredictorInformation access />} />,*/}
              {/*]}*/}

              {canView(accessNames.battleOfSchools) && [
                <Route
                  path={routePaths.organizationsCallForFootball + "/*"}
                  element={<OrganizationsCallForFootball access />}
                />,
                <Route
                  path={routePaths.representativesCallForFootball + "/*"}
                  element={<RepresentativesCallForFootball access />}
                />,
                <Route
                  path={routePaths.devisionsCallForFootball + "/*"}
                  element={<DevisionsCallForFootball access />}
                />,
                <Route
                  path={routePaths.representativeDetailsCallForFootball() + "/*"}
                  element={<RepresentativeDetailsCallForFootball access />}
                />,
                <Route
                  path={routePaths.participantsCallForFootball + "/*"}
                  element={<ParticipantsCallForFootball access />}
                />,
                <Route path={routePaths.tagsCallForFootball + "/*"} element={<TagsCallForFootball access />} />,
                <Route path={routePaths.levelsCallForFootball + "/*"} element={<LevelsCallForFootball access />} />,
                <Route
                  path={routePaths.matchApplicationCallForFootball + "/*"}
                  element={<MatchApplicationCallForFootball access />}
                />,
                <Route
                  path={routePaths.matchApplications() + "/*"}
                  element={<MatchApplicationsCallForFootball access />}
                />,
                <Route
                  path={routePaths.matchApplicationsOrganization() + "/*"}
                  element={<MatchApplicationsParticipantsCallForFootball access />}
                />,
              ]}

              {canView(accessNames.stock) && (
                <Route path={routePaths.bestPlayer + "/*"} element={<BestPlayer access />} />
              )}

              {canView(accessNames.banner) && (
                <Route path={routePaths.advertising + "/*"} element={<Banners access />} />
              )}

              {canView(accessNames.role) && [
                <Route key={1} path={routePaths.employee + "/*"} element={<EmployeeView access />} />,
                <Route key={2} path={routePaths.employeeRole + "/*"} element={<EmployeeRole access />} />,
              ]}

              {canView(accessNames.fullAccess) && (
                <Route path={routePaths.usersView + "/*"} element={<UsersView access />} />
              )}

              {canView(accessNames.audit) && <Route path={routePaths.systemLog + "/*"} element={<SystemLog />} />}

              {canView(accessNames.loyalty) && (
                <Route
                  path={routePaths.loyalty + "/*"}
                  element={<Loyalty access={canView(`${accessNames.loyalty}`)} />}
                />
              )}
            </>
          )}
        </Route>

        <Route path="*" element={<Redirect path={routePaths.base} />} />
      </Routes>
    </Suspense>
  );
};

const FallBack = styled.div`
  background-color: rgb(240, 242, 245);
`;
